import { Loader2 } from 'lucide-react';
import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

import { Text } from '@/components/Text';
import { buttonVariants } from '@/components/ui/button';
import { restService } from '@/lib/utils/rest-service';
import { useAccessDeniedStore } from '@/store/access-denied';
import { useImpersonationStore } from '@/store/impersonation';

/**
 * Shown by PermissionGuard in place of a page whose own API request was
 * refused (403) while the menu still lists it: the token is renewed up to
 * three times, then the denial is final until the route changes.
 */
export const AccessDeniedState = () => {
  // Component stores
  const { phase, attempts } = useAccessDeniedStore();
  const isImpersonating = useImpersonationStore((s) => s.isImpersonating);

  // Component defaults
  const isDenied = isImpersonating || phase === 'denied';
  // one run per attempt, also across a StrictMode remount of the effect
  const run = useRef<{
    attempt: number;
    controller: AbortController;
    keep: boolean;
  } | null>(null);

  useEffect(() => {
    // a refresh would swap the impersonated token for the admin's
    if (isImpersonating || phase !== 'updating') return;

    let current = run.current;

    if (current?.attempt === attempts && !current.controller.signal.aborted) {
      // the remount of the same attempt: its run goes on
      current.keep = true;
    } else {
      const controller = new AbortController();
      const started = { attempt: attempts, controller, keep: true };
      const { path } = useAccessDeniedStore.getState();

      run.current = started;
      current = started;

      void restService
        .retryAfterForbidden(controller.signal)
        .then((changed) => {
          // dropped while it waited: nobody is waiting for this result
          if (controller.signal.aborted) return;

          const denied = useAccessDeniedStore.getState();

          // the route changed or another attempt took over while we waited
          if (denied.path !== path || denied.attempts !== attempts) return;

          if (changed) denied.attemptSucceeded();
          else denied.attemptFailed();
        });
    }

    const mine = current;

    return () => {
      // StrictMode runs this cleanup and the effect again in the same tick:
      // abort one microtask later, unless the effect took the run back
      mine.keep = false;
      queueMicrotask(() => {
        if (!mine.keep) mine.controller.abort();
      });
    };
  }, [isImpersonating, phase, attempts]);

  return (
    <div className='w-full h-screen-minus-padding flex flex-col items-center justify-center gap-4 text-center'>
      {isDenied ? (
        <>
          <Text renderAs='h3'>You don't have access to this page</Text>

          <Text renderAs='psmall' className='text-greyDark_60'>
            If you need access to this page, contact your administrator.
          </Text>

          <Link
            to='/'
            className={buttonVariants({ isOutline: true, className: 'mt-2' })}
          >
            <Text renderAs='ctabold' className='text-inherit'>
              Go to the dashboard
            </Text>
          </Link>
        </>
      ) : (
        <>
          <Loader2 className='w-8 h-8 animate-spin stroke-greenLight' />

          <Text renderAs='h3'>Your access is being updated…</Text>

          <Text renderAs='psmall' className='text-greyDark_60'>
            This can take up to two minutes.
          </Text>
        </>
      )}
    </div>
  );
};
